/**
 * DecklineNameInput component
 * Renders the deckline name input field(s) based on the deckline type.
 * - Standard mode: single "Deckline Name" field
 * - Linked KPI mode (BASE_KPI): Autocomplete for Root KPI selection + Autocomplete for Variant
 */

import './DecklineNameInput.scss';

import { useAurigaContext } from '@context/AurigaContext';
import { useKPILibraryContext } from '@context/KPILibraryContext';
import { usePageContext } from '@context/PageContext';
import ContentPasteSearchIcon from '@mui/icons-material/ContentPasteSearch';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import { DatasetSource } from '@services/api/deckManagement/graphql/schema/API';
import { useGetVariantsForRootKPI } from '@services/api/deckManagement/hooks/useKPIVariants';
import { resolveDatasetSourceLabel } from '@utils/enumLabelHelpers';
import { datasetSourceFromDrivers } from '@utils/kpiDriverHelpers';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { DecklineNameInputProps, RootKPIOption, VariantOption } from 'types/DecklineNameInput.types';
import { AnyKPIDrivers, PageSidePanelDropDown } from 'types/PageSidePanel.types';

const rootKpiFilter = createFilterOptions<RootKPIOption>();
const variantFilter = createFilterOptions<VariantOption>();

const BASE_CLASS_NAME = 'deckline-name-input';

/** Shown under the (disabled) Variant input when the variant list fails to load with nothing cached. */
const VARIANTS_LOAD_ERROR = "Couldn't load variants. Refresh the page to try again.";

/**
 * Cleans a typed variant name into the form it is created and displayed with: trim, then collapse any
 * interior whitespace run to a single space. Case is kept.
 * @param s - the raw typed name
 * @returns the cleaned name (e.g. `"  ZZZ New.   2 "` ⇒ `"ZZZ New. 2"`)
 */
const cleanVariantName = (s: string): string => s.trim().replace(/\s+/g, ' ');

/**
 * Builds the error shown when a typed variant name matches a variant whose dataset the page does not
 * support.
 * @param option - the matched unsupported variant option
 * @returns user-facing error text naming the variant and its dataset
 */
const buildUnsupportedVariantError = (option: VariantOption): string => {
  const datasetLabel = option.datasetSource ? resolveDatasetSourceLabel(option.datasetSource) : 'another';
  return `Variant "${option.variant_name.trim()}" uses the ${datasetLabel} dataset, which this page does not support.`;
};

const DecklineNameInput = ({
  value,
  rootKpiId = null,
  kpiVariantId = null,
  decklineType,
  onNameChange,
  onLinkedKpiChange,
  onOpenKPILibrary,
  newVariantDataset,
  supportedDatasets,
  rootLabel = 'Deckline Name',
  disabled = false,
}: DecklineNameInputProps) => {
  const isLinkedKpiMode = decklineType === PageSidePanelDropDown.BASE_KPI;

  // Datasets the page is configured for. Root KPIs are always shown; a variant whose resolved dataset is
  // not configured here is disabled (not hidden) in the Variant selector.
  const { configuredDatasets } = usePageContext();

  /** Datasets the page supports, as a set for membership tests. Empty ⇒ every variant treated as supported. */
  const configuredDatasetSet = useMemo(
    () => new Set(supportedDatasets ?? configuredDatasets),
    [supportedDatasets, configuredDatasets],
  );

  const { featureFlags } = useAurigaContext();
  const enableKPICreation = featureFlags.enableKPILibraryCreate;

  const { pendingCreations, isLoading: isRootKPIsLoading } = useKPILibraryContext();
  const {
    allRootKPIs,
    pendingVariants: allPendingVariants,
    pendingRootKPIs,
    addPendingRootKPI,
    addPendingVariant,
    getEnhancedVariants,
  } = pendingCreations;

  /** Tracks the selected Root KPI ID for fetching child variants */
  const [localSelectedRootKpiId, setLocalSelectedRootKpiId] = useState<string | null>(rootKpiId);

  /** Sync internal state when the selectedDeckline changes (rootKpiId prop updates) */
  useEffect(() => {
    setLocalSelectedRootKpiId(rootKpiId);
  }, [rootKpiId]);

  /**
   * Root KPI options for the Autocomplete.
   *
   * Every Root KPI (persisted and pending) is shown, independent of its variants' `dataset_source` — a
   * customer must be able to find any Root KPI that exists, regardless of which datasets its variants
   * target (the Variant selector handles the per-dataset constraint). Options stay sorted by name and
   * pending (new) Root KPIs stay tagged `isNew`.
   */
  const rootKpiOptions = useMemo(
    (): RootKPIOption[] =>
      allRootKPIs
        .map((kpi) => ({
          root_kpi_id: kpi.root_kpi_id,
          root_kpi_name: kpi.root_kpi_name,
          isNew: pendingRootKPIs.some((p) => p.root_kpi_id === kpi.root_kpi_id),
        }))
        .sort((a, b) => a.root_kpi_name.localeCompare(b.root_kpi_name)),
    [allRootKPIs, pendingRootKPIs],
  );

  const {
    data: fetchedVariants,
    isFetching: isVariantsFetching,
    isError: isVariantsError,
  } = useGetVariantsForRootKPI({
    root_kpi_id: localSelectedRootKpiId ?? '',
    options: { enabled: !!localSelectedRootKpiId },
  });

  /** The selected Root KPI's fetched variants; `[]` until a successful load. */
  const variants = useMemo(() => fetchedVariants ?? [], [fetchedVariants]);

  /** A pending (new, unsaved) Root KPI has no persisted variants — the backend has none to return. */
  const isPendingRootKpi = useMemo(
    () => pendingRootKPIs.some((p) => p.root_kpi_id === localSelectedRootKpiId),
    [pendingRootKPIs, localSelectedRootKpiId],
  );

  /**
   * True while the selected Root KPI's variant list has never loaded successfully. In that window
   * `variants` is the `[]` fallback, so the name index and unsupported-variant check would be empty — the
   * Variant input is disabled so a typed name can't duplicate (or bypass the block on) a variant that
   * hasn't loaded. Keyed on "has data", which covers:
   *  - the first load (pending) and a failed first load (error, no data) ⇒ disabled;
   *  - a background refetch, successful or failed, that still holds cached data ⇒ stays enabled (no
   *    flicker, no lost focus mid-typing).
   * A pending (new) Root KPI is exempt: it has no persisted variants (its fetch can error), and its pending
   * variants are local, so the name index is already complete.
   */
  const areVariantsUnloaded = !!localSelectedRootKpiId && !isPendingRootKpi && fetchedVariants === undefined;

  /** The variant list failed to load and there is nothing cached — explains the disabled input. */
  const showVariantsLoadError = areVariantsUnloaded && isVariantsError && !isVariantsFetching;

  /**
   * Resolves a variant's effective {@link DatasetSource} the same way `PageSidePanel` does: the explicit
   * `dataset_source`, else the dataset implied by its overrides' shape (HCTR-shaped ⇒ HCTR, otherwise
   * GALAXY). A null/missing `dataset_source` is therefore NEVER unsupported-by-default — it resolves to the
   * page's single-dataset default.
   */
  const resolveVariantDataset = useCallback(
    (variant: { dataset_source?: DatasetSource | null; variant_overrides?: AnyKPIDrivers | null }): DatasetSource =>
      variant.dataset_source ?? datasetSourceFromDrivers(variant.variant_overrides),
    [],
  );

  /**
   * Merges existing variants with pending variants for the selected Root KPI, sorted by name.
   *
   * Every variant is shown (no dataset filter). An existing variant is tagged `isUnsupported` when its
   * resolved dataset is not one the page is configured for, so the Autocomplete can disable it with an
   * explanatory dataset chip rather than hiding it. The currently-linked variant is always forced
   * selectable so an already-saved off-dataset link is never blocked (AC2.7). Pending (new) variants are
   * always selectable (AC2.8). With no configured datasets, nothing is unsupported (AC2.6). The resolved
   * dataset is carried on every option to drive the dataset chip.
   */
  const variantOptions = useMemo((): VariantOption[] => {
    const existing: VariantOption[] = variants.map((v) => {
      const resolvedDataset = resolveVariantDataset(v);
      const supported =
        v.kpi_variant_id === kpiVariantId || // never block the saved selection
        configuredDatasetSet.size === 0 ||
        configuredDatasetSet.has(resolvedDataset);
      return {
        kpi_variant_id: v.kpi_variant_id,
        variant_name: v.variant_name,
        isUnsupported: !supported,
        datasetSource: resolvedDataset,
      };
    });
    const pending: VariantOption[] = allPendingVariants
      .filter((p) => p.root_kpi_id === localSelectedRootKpiId)
      .map((p) => ({
        kpi_variant_id: p.kpi_variant_id,
        variant_name: p.name,
        isNew: true,
        datasetSource: p.dataset_source ?? newVariantDataset,
      }));
    return [...existing, ...pending].sort((a, b) => a.variant_name.localeCompare(b.variant_name));
  }, [
    variants,
    allPendingVariants,
    localSelectedRootKpiId,
    configuredDatasetSet,
    kpiVariantId,
    newVariantDataset,
    resolveVariantDataset,
  ]);

  /**
   * Normalizes a variant name for conflict detection: trim, collapse any interior whitespace run to a
   * single space, lowercase.
   */
  const normalizeVariantName = (s: string): string => cleanVariantName(s).toLowerCase();

  /**
   * Two-stage name index for the selected Root KPI's variants (fetched + pending), dataset-independent:
   *  - `raw` — verbatim stored names (stage 1, exact-match selection guard, NO trimming)
   *  - `normalized` — normalized keys (stage 2, conflict/duplicate guard)
   *
   * Both the Create entry (`filterVariantOptions`) and the blur handler (`handleVariantBlur`) use this —
   * never the display-partitioned `variantOptions` — so an off-dataset/disabled variant still blocks a
   * duplicate create, and a stored name with trailing whitespace (e.g. `"L60032 "`) is matched exactly
   * rather than re-created.
   */
  const variantNameIndex = useMemo(() => {
    const raw = new Set<string>();
    const normalized = new Set<string>();
    const add = (name: string) => {
      raw.add(name);
      normalized.add(normalizeVariantName(name));
    };
    variants.forEach((v) => add(v.variant_name));
    allPendingVariants.filter((p) => p.root_kpi_id === localSelectedRootKpiId).forEach((p) => add(p.name));
    return { raw, normalized };
  }, [variants, allPendingVariants, localSelectedRootKpiId]);

  /**
   * Classifies a typed variant input against {@link variantNameIndex}:
   *  - `'existing'` — a RAW exact match (no trimming): the input IS that stored variant ⇒ resolve to
   *    selection, never create.
   *  - `'conflict'` — a NORMALIZED collision: a duplicate of an existing name ⇒ block, never create.
   *  - `'create'`   — neither ⇒ offer/create a new pending variant (when creation is enabled).
   */
  const classifyVariantInput = useCallback(
    (rawInput: string): 'existing' | 'conflict' | 'create' => {
      if (variantNameIndex.raw.has(rawInput)) return 'existing';
      if (variantNameIndex.normalized.has(normalizeVariantName(rawInput))) return 'conflict';
      return 'create';
    },
    [variantNameIndex],
  );

  /**
   * Finds the unsupported (disabled) variant option a typed input refers to, if any. Matches on the
   * normalized name — variant names are unique within a Root KPI, so at most one option can match. The
   * currently-linked variant and pending variants are never unsupported (see {@link variantOptions}), so
   * they are never returned here.
   */
  const findUnsupportedVariantMatch = useCallback(
    (rawInput: string): VariantOption | null => {
      const key = normalizeVariantName(rawInput);
      return variantOptions.find((o) => o.isUnsupported && normalizeVariantName(o.variant_name) === key) ?? null;
    },
    [variantOptions],
  );

  /** Error text for the Variant input (e.g. a typed name that matches an unsupported variant). */
  const [variantError, setVariantError] = useState<string | null>(null);

  /**
   * Set when a blocked input clears the linked variant, so the selection-sync effect below keeps the
   * user's typed text and error instead of blanking them when the selection drops to null. Consumed once.
   */
  const preserveInputOnClearRef = useRef(false);

  // TODO: Root KPI list may be incomplete until getRootKPIs API supports nextToken pagination input.
  /** The currently selected Root KPI option, derived from selectedRootKpiId state */
  const selectedRootKpi = useMemo((): RootKPIOption | null => {
    if (!localSelectedRootKpiId) return null;
    return rootKpiOptions.find((kpi) => kpi.root_kpi_id === localSelectedRootKpiId) ?? null;
  }, [rootKpiOptions, localSelectedRootKpiId]);

  /** Controlled input value for the Root KPI Autocomplete */
  const [rootInputValue, setRootInputValue] = useState('');

  /** Sync input text when the resolved Root KPI changes */
  useEffect(() => {
    setRootInputValue(selectedRootKpi?.root_kpi_name ?? '');
  }, [selectedRootKpi]);

  /** The currently selected variant option, derived from the kpiVariantId prop */
  const selectedVariantValue = useMemo((): VariantOption | null => {
    if (!kpiVariantId) return null;
    if (isVariantsFetching) return null;
    return variantOptions.find((v) => v.kpi_variant_id === kpiVariantId) ?? null;
  }, [variantOptions, kpiVariantId, isVariantsFetching]);

  /** Controlled input value for the Variant Autocomplete */
  const [variantInputValue, setVariantInputValue] = useState('');

  /**
   * A new Root KPI means a new variant list — drop any error about the previous one. Declared before the
   * selection-sync effect so a pending "preserve" flag never survives a Root KPI change.
   */
  useEffect(() => {
    preserveInputOnClearRef.current = false;
    setVariantError(null);
  }, [localSelectedRootKpiId]);

  /**
   * Sync input text when the resolved Variant changes, clearing any stale error — except right after a
   * blocked input cleared the selection, where the typed text and its error must stay visible.
   */
  useEffect(() => {
    const preserve = preserveInputOnClearRef.current && !selectedVariantValue;
    preserveInputOnClearRef.current = false;
    if (preserve) return;
    setVariantInputValue(selectedVariantValue?.variant_name ?? '');
    setVariantError(null);
  }, [selectedVariantValue]);

  /**
   * Blocks a typed variant name that matches an unsupported variant: shows the error and returns true so
   * the caller stops (no selection, no creation). If a variant is currently linked (e.g. a pending one the
   * user created a moment ago), it is unlinked — the Variant selection governs the rest of the side panel,
   * so a blocked name must not leave the previous variant's create/driver workflow on screen. Returns false
   * when the input is not blocked.
   */
  const blockIfUnsupportedVariant = useCallback(
    (rawInput: string): boolean => {
      const match = findUnsupportedVariantMatch(rawInput);
      if (!match) return false;
      setVariantError(buildUnsupportedVariantError(match));
      if (kpiVariantId && localSelectedRootKpiId) {
        // Only arm the flag when the clear will actually move the selection to null (otherwise the sync
        // effect never runs and the flag would linger).
        if (selectedVariantValue) preserveInputOnClearRef.current = true;
        onLinkedKpiChange?.({ id: localSelectedRootKpiId, name: selectedRootKpi?.root_kpi_name ?? null }, null);
      }
      return true;
    },
    [
      findUnsupportedVariantMatch,
      kpiVariantId,
      localSelectedRootKpiId,
      selectedVariantValue,
      onLinkedKpiChange,
      selectedRootKpi,
    ],
  );

  /** Handles selection of a Root KPI from the Autocomplete */
  const handleRootKpiSelect = useCallback(
    (_event: React.SyntheticEvent, selected: RootKPIOption | string | null) => {
      if (typeof selected === 'string') {
        if (!enableKPICreation) return;
        const trimmed = selected.trim();
        const newId = addPendingRootKPI(trimmed);
        setLocalSelectedRootKpiId(newId);
        onLinkedKpiChange?.({ id: newId, name: trimmed }, null);
        setRootInputValue(trimmed);
      } else if (selected?.isNew) {
        if (!enableKPICreation) return;
        const newId = addPendingRootKPI(selected.root_kpi_name);
        setLocalSelectedRootKpiId(newId);
        onLinkedKpiChange?.({ id: newId, name: selected.root_kpi_name }, null);
        setRootInputValue(selected.root_kpi_name);
      } else {
        setLocalSelectedRootKpiId(selected?.root_kpi_id ?? null);
        onLinkedKpiChange?.({ id: selected?.root_kpi_id ?? null, name: selected?.root_kpi_name ?? null }, null);
        setRootInputValue(selected?.root_kpi_name ?? '');
      }
    },
    [onLinkedKpiChange, addPendingRootKPI, enableKPICreation],
  );

  /** Handles selection of a Variant from the Autocomplete */
  const handleVariantSelect = useCallback(
    (_event: React.SyntheticEvent, selected: VariantOption | string | null) => {
      if (!localSelectedRootKpiId) return;
      // Defensive: MUI blocks selection of a disabled option, so this is unreachable for unsupported
      // variants — kept to document intent and guard against a future non-Autocomplete caller.
      if (typeof selected !== 'string' && selected?.isUnsupported) return;
      const rootKpiPayload = { id: localSelectedRootKpiId, name: selectedRootKpi?.root_kpi_name ?? null };
      const enhancedList = getEnhancedVariants(localSelectedRootKpiId, variants);

      if (typeof selected === 'string') {
        // Same order as handleVariantBlur: block ⇒ existing ⇒ conflict ⇒ (creation gate) ⇒ create. Only the
        // create step is gated by enableKPICreation, so selecting an existing name works on any page.
        // A typed name matching an unsupported variant is blocked with an error, regardless of creation.
        if (blockIfUnsupportedVariant(selected)) return;
        // Route the freeSolo string commit (highlight + Enter, or MUI committing the typed value)
        // through the SAME two-stage guard as blur/filter — never the display-partitioned options — so
        // an existing name (supported, unsupported/disabled, or pending) resolves to its stored variant
        // or no-ops instead of creating a duplicate.
        const classification = classifyVariantInput(selected);
        if (classification === 'existing') {
          // Resolve against fetched AND pending variants (the name index covers both).
          const enhanced = enhancedList.find((v) => v.variant_name === selected);
          if (enhanced && enhanced.kpi_variant_id !== kpiVariantId) onLinkedKpiChange?.(rootKpiPayload, enhanced);
          return;
        }
        if (classification === 'conflict') return; // duplicate of an existing name — do not create
        if (!enableKPICreation) return; // only the create path is gated
        const cleaned = cleanVariantName(selected);
        const enhanced = addPendingVariant(localSelectedRootKpiId, cleaned, newVariantDataset);
        setVariantInputValue(cleaned);
        onLinkedKpiChange?.(rootKpiPayload, enhanced);
      } else if (selected?.isNew) {
        if (!enableKPICreation) return;
        const cleaned = cleanVariantName(selected.variant_name);
        const enhanced = addPendingVariant(localSelectedRootKpiId, cleaned, newVariantDataset);
        setVariantInputValue(cleaned);
        onLinkedKpiChange?.(rootKpiPayload, enhanced);
      } else {
        const enhanced = enhancedList.find((v) => v.kpi_variant_id === selected?.kpi_variant_id) ?? null;
        onLinkedKpiChange?.(rootKpiPayload, enhanced);
      }
    },
    [
      onLinkedKpiChange,
      localSelectedRootKpiId,
      selectedRootKpi,
      addPendingVariant,
      enableKPICreation,
      getEnhancedVariants,
      variants,
      newVariantDataset,
      classifyVariantInput,
      blockIfUnsupportedVariant,
      kpiVariantId,
    ],
  );

  /** Filters Root KPI options, prepending a [CREATE] entry when input doesn't match existing */
  const filterRootKpiOptions = useCallback(
    (options: RootKPIOption[], params: { inputValue: string; getOptionLabel: (option: RootKPIOption) => string }) => {
      const trimmed = params.inputValue.trim();
      const filtered = rootKpiFilter(options, { ...params, inputValue: trimmed });
      if (enableKPICreation && trimmed && !allRootKPIs.some((kpi) => kpi.root_kpi_name === trimmed)) {
        filtered.unshift({ root_kpi_id: null, root_kpi_name: trimmed, isNew: true });
      }
      return filtered;
    },
    [allRootKPIs, enableKPICreation],
  );

  /** Filters variant options, prepending a [CREATE] entry only when the typed name is genuinely new */
  const filterVariantOptions = useCallback(
    (options: VariantOption[], params: { inputValue: string; getOptionLabel: (option: VariantOption) => string }) => {
      // Stage 1 matches the RAW input (no trim); the display label and the fuzzy filter use the cleaned value,
      // so the [CREATE] entry shows (and creates) exactly the name that will be stored.
      const raw = params.inputValue;
      const cleaned = cleanVariantName(raw);
      const filtered = variantFilter(options, { ...params, inputValue: cleaned });
      if (enableKPICreation && cleaned && classifyVariantInput(raw) === 'create') {
        filtered.unshift({ kpi_variant_id: null, variant_name: cleaned, isNew: true });
      }
      return filtered;
    },
    [classifyVariantInput, enableKPICreation],
  );

  /** On blur, resolve unmatched input: create if enabled, otherwise clear */
  const handleRootKpiBlur = useCallback(() => {
    const trimmed = rootInputValue.trim();
    if (!trimmed) return;

    // Match against the unfiltered list: an existing (but off-dataset, filtered-out) Root KPI must not
    // trigger a duplicate creation.
    const matchesOption = allRootKPIs.some((kpi) => kpi.root_kpi_name === trimmed);
    if (matchesOption) return;

    if (enableKPICreation) {
      const newId = addPendingRootKPI(trimmed);
      setLocalSelectedRootKpiId(newId);
      onLinkedKpiChange?.({ id: newId, name: trimmed }, null);
      setRootInputValue(trimmed);
    } else {
      // Clear to last valid selection
      setRootInputValue(selectedRootKpi?.root_kpi_name ?? '');
    }
  }, [rootInputValue, allRootKPIs, enableKPICreation, addPendingRootKPI, onLinkedKpiChange, selectedRootKpi]);

  /**
   * On blur, resolve the typed variant input. First, a name matching an UNSUPPORTED variant (exact or
   * normalized) is blocked with an error — no selection, no creation. Otherwise, via the two-stage
   * classifier:
   *  - `'existing'` — raw exact match ⇒ select that stored variant (fire `onLinkedKpiChange` with it),
   *    never create. Fixes the trailing-whitespace duplicate-create bug (e.g. `"L60032 "`).
   *  - `'conflict'` — normalized duplicate ⇒ no-op (leave the input; do not create).
   *  - `'create'`   — genuinely new ⇒ create a pending variant with the cleaned name (trimmed, interior
   *    whitespace collapsed) and show that same name in the input when creation is
   *    enabled, otherwise revert to the last valid selection.
   */
  const handleVariantBlur = useCallback(() => {
    const raw = variantInputValue;
    const cleaned = cleanVariantName(raw);
    // Clean the visible text on blur for paths that keep the typed input. Safe from loops: setting the
    // controlled `inputValue` does not fire `onInputChange`, and the classifiers read `raw`, not the state.
    const cleanInputValue = () => {
      if (raw !== cleaned) setVariantInputValue(cleaned);
    };
    if (!localSelectedRootKpiId) return;
    if (!cleaned) {
      cleanInputValue();
      return;
    }

    if (blockIfUnsupportedVariant(raw)) {
      cleanInputValue();
      return;
    }

    const classification = classifyVariantInput(raw);
    const rootKpiPayload = { id: localSelectedRootKpiId, name: selectedRootKpi?.root_kpi_name ?? null };

    if (classification === 'existing') {
      // The input IS an existing variant — resolve it to its enhanced form and select it. Fetched AND pending
      // variants are searched (the name index covers both). Already linked ⇒ nothing to change.
      const enhanced = getEnhancedVariants(localSelectedRootKpiId, variants).find((v) => v.variant_name === raw);
      if (enhanced && enhanced.kpi_variant_id !== kpiVariantId) onLinkedKpiChange?.(rootKpiPayload, enhanced);
      return;
    }

    if (classification === 'conflict') {
      cleanInputValue(); // duplicate of an existing name — do not create
      return;
    }

    if (enableKPICreation) {
      const enhanced = addPendingVariant(localSelectedRootKpiId, cleaned, newVariantDataset);
      onLinkedKpiChange?.(rootKpiPayload, enhanced);
      setVariantInputValue(cleaned);
    } else {
      setVariantInputValue(selectedVariantValue?.variant_name ?? '');
    }
  }, [
    variantInputValue,
    blockIfUnsupportedVariant,
    classifyVariantInput,
    variants,
    getEnhancedVariants,
    kpiVariantId,
    enableKPICreation,
    addPendingVariant,
    localSelectedRootKpiId,
    selectedRootKpi,
    onLinkedKpiChange,
    selectedVariantValue,
    newVariantDataset,
  ]);

  if (!isLinkedKpiMode) {
    return (
      <Box className={`${BASE_CLASS_NAME}__standard`}>
        <TextField
          fullWidth
          value={value}
          onChange={(e) => onNameChange?.(e.target.value)}
          label='Deckline Name'
          margin='dense'
          autoComplete='off'
          size='small'
          disabled={disabled}
        />
      </Box>
    );
  }

  return (
    <Box className={`${BASE_CLASS_NAME}__linked-kpi`}>
      <Stack spacing={1} useFlexGap direction='row' className={`${BASE_CLASS_NAME}__linked-kpi-row`}>
        <Autocomplete<RootKPIOption, false, false, true>
          options={rootKpiOptions}
          getOptionLabel={(option) => (typeof option === 'string' ? option : option.root_kpi_name)}
          // Destructure key from props — MUI provides an auto-generated key, but we override with a stable identifier
          renderOption={({ key: _key, ...props }, option) => (
            <li key={option.root_kpi_id ?? `create-${option.root_kpi_name}`} {...props}>
              {option.root_kpi_id === null ? `NEW: "${option.root_kpi_name}"` : option.root_kpi_name}
            </li>
          )}
          value={selectedRootKpi}
          inputValue={rootInputValue}
          onInputChange={(_event, newValue, reason) => {
            if (reason !== 'reset') setRootInputValue(newValue);
          }}
          onChange={handleRootKpiSelect}
          onBlur={handleRootKpiBlur}
          filterOptions={filterRootKpiOptions}
          loading={isRootKPIsLoading}
          disabled={disabled}
          size='small'
          freeSolo
          fullWidth
          autoHighlight
          isOptionEqualToValue={(option, val) => typeof val !== 'string' && option.root_kpi_id === val.root_kpi_id}
          renderInput={(params) => (
            <TextField
              {...params}
              label={rootLabel}
              margin='dense'
              helperText={selectedRootKpi?.isNew ? '✓ New Root KPI' : undefined}
              slotProps={{
                ...params.slotProps,
                formHelperText: selectedRootKpi?.isNew
                  ? { className: `${BASE_CLASS_NAME}__helper-text-success` }
                  : undefined,
                input: {
                  ...params.slotProps.input,
                  endAdornment: isRootKPIsLoading ? (
                    <InputAdornment position='end'>
                      <CircularProgress size={14} />
                    </InputAdornment>
                  ) : (
                    params.slotProps.input.endAdornment
                  ),
                },
              }}
            />
          )}
        />
        <IconButton
          aria-label='Open KPI Library'
          disabled={disabled || selectedRootKpi?.isNew}
          className={`${BASE_CLASS_NAME}__kpi-library-dialog-button`}
          onClick={onOpenKPILibrary}
          tabIndex={-1}
        >
          <ContentPasteSearchIcon />
        </IconButton>
      </Stack>
      <Autocomplete<VariantOption, false, false, true>
        options={variantOptions}
        getOptionLabel={(option) => (typeof option === 'string' ? option : option.variant_name)}
        getOptionDisabled={(option) => !!option.isUnsupported}
        // Destructure key from props — MUI provides an auto-generated key, but we override with a stable identifier
        renderOption={({ key: _key, className, ...props }, option) => (
          <li
            key={option.kpi_variant_id ?? `create-${option.variant_name}`}
            className={`${className ?? ''} ${BASE_CLASS_NAME}__option-row`.trim()}
            {...props}
          >
            <span className={`${BASE_CLASS_NAME}__option-main`}>
              {option.kpi_variant_id === null ? `NEW: "${option.variant_name}"` : option.variant_name}
            </span>
            {option.datasetSource && (
              <Chip
                label={resolveDatasetSourceLabel(option.datasetSource)}
                size='small'
                className={`${BASE_CLASS_NAME}__dataset-chip`}
                aria-hidden
              />
            )}
          </li>
        )}
        value={selectedVariantValue}
        inputValue={variantInputValue}
        onInputChange={(_event, newValue, reason) => {
          if (reason === 'reset') return;
          setVariantInputValue(newValue);
          setVariantError(null); // the user is editing — drop the previous error
        }}
        onChange={handleVariantSelect}
        onBlur={handleVariantBlur}
        filterOptions={filterVariantOptions}
        loading={isVariantsFetching}
        // Off until the Root KPI's variants first load, so typed commits always run against the full list.
        disabled={disabled || !localSelectedRootKpiId || areVariantsUnloaded}
        size='small'
        freeSolo
        autoHighlight
        isOptionEqualToValue={(option, val) => typeof val !== 'string' && option.kpi_variant_id === val.kpi_variant_id}
        renderInput={(params) => (
          <TextField
            {...params}
            label='Variant'
            margin='dense'
            error={!!variantError || showVariantsLoadError}
            helperText={
              variantError ??
              (showVariantsLoadError ? VARIANTS_LOAD_ERROR : undefined) ??
              (selectedVariantValue?.isNew ? '✓ New Variant' : undefined)
            }
            slotProps={{
              ...params.slotProps,
              formHelperText:
                !variantError && !showVariantsLoadError && selectedVariantValue?.isNew
                  ? { className: `${BASE_CLASS_NAME}__helper-text-success` }
                  : undefined,
              input: {
                ...params.slotProps.input,
                endAdornment: isVariantsFetching ? (
                  <InputAdornment position='end'>
                    <CircularProgress size={14} />
                  </InputAdornment>
                ) : (
                  params.slotProps.input.endAdornment
                ),
              },
            }}
          />
        )}
      />
    </Box>
  );
};

export default DecklineNameInput;
